import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Game } from "@/types/game";
import { repairGameCovers } from "@/components/admin/games/dialog/services/gameService";

export const useGamesAdmin = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [repairing, setRepairing] = useState(false);

  const fetchGames = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("games")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) {
        console.error("Erro ao buscar jogos:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar a lista de jogos.",
          variant: "destructive",
        });
      } else {
        // Log games data for debugging
        console.log("Games data loaded (admin):", data);
        console.log("Games with missing covers:", data?.filter(game => !game.cover_url).length);
        console.log("Total active games found:", data?.length || 0);
        setGames(data || []);
      }
    } catch (error) {
      console.error("Erro inesperado:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, []);

  const openAddDialog = () => {
    setIsEditMode(false);
    setCurrentGame(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (game: Game) => {
    setIsEditMode(true);
    setCurrentGame(game);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este jogo?")) {
      return;
    }

    try {
      const { error } = await supabase.from("games").delete().eq("id", id);

      if (error) {
        throw error;
      }

      toast({
        title: "Sucesso",
        description: "Jogo excluído com sucesso!",
      });
      
      // Refresh games list
      fetchGames();
    } catch (error) {
      console.error("Erro ao excluir jogo:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao excluir o jogo.",
        variant: "destructive",
      });
    }
  };

  const repairCovers = async () => {
    try {
      setRepairing(true);
      toast({
        title: "Iniciando reparo",
        description: "Tentando reparar URLs de imagens de jogos...",
      });
      
      await repairGameCovers();
      
      toast({
        title: "Concluído",
        description: "Processo de reparo de imagens concluído.",
      });
      
      // Refresh the games list
      fetchGames();
    } catch (error) {
      console.error("Erro ao reparar capas:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao tentar reparar as imagens.",
        variant: "destructive",
      });
    } finally {
      setRepairing(false);
    }
  };

  const filteredGames = games.filter(game => 
    game.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    games: filteredGames,
    loading,
    repairing,
    isDialogOpen,
    setIsDialogOpen,
    isEditMode,
    currentGame,
    searchTerm,
    setSearchTerm,
    openAddDialog,
    openEditDialog,
    handleDelete,
    fetchGames,
    repairCovers
  };
};
